// 商品匯入 / 匯出 (CSV). Same columns both ways, so a shop can export, edit in
// Excel / Numbers and import back. Rows match existing products by barcode,
// then SKU. Nothing is written until the owner confirms the preview. Stock
// goes through adjust_stock (opening for new products, count for existing
// ones when 「更新庫存」 is on) so it stays audited.
// Also reads a WooCommerce product export (Name / SKU / Regular price …).
import { useMemo, useState } from "react";
import { zhEn } from "./posTier.js";
import { Download, Upload, FileText } from "lucide-react";
import { C, Btn, Pill, Chip, Seg, Sheet, Label, money, round2 } from "./posUi.jsx";
import { newId, posErrorText } from "./posMath.js";
import { bulkUpsertPosProducts, adjustStock } from "../../lib/api.js";

export const COLUMNS = [
  ["name", zhEn("名稱", "Name"), zhEn("必填", "required")],
  ["barcode", zhEn("條碼", "Barcode"), ""],
  ["sku", "SKU", ""],
  ["category", zhEn("分類", "Category"), ""],
  ["brand", zhEn("品牌", "Brand"), ""],
  ["price", zhEn("售價", "Price"), zhEn("必填", "required")],
  ["sale_price", zhEn("特價", "Sale price"), ""],
  ["cost", zhEn("成本", "Cost"), ""],
  ["stock", zhEn("庫存", "Stock"), ""],
  ["low_stock", zhEn("低庫存提示", "Low stock amount"), ""],
  ["status", zhEn("狀態", "Status"), zhEn("上架 / 隱藏 / 下架", "active / hidden / draft")],
  ["sellable", zhEn("可賣", "Available"), zhEn("是 / 否", "yes / no")],
  ["track_stock", zhEn("追蹤庫存", "Track stock"), zhEn("是 / 否", "yes / no")],
  ["description", zhEn("描述", "Description"), ""],
  ["image_url", zhEn("圖片網址", "Image URL"), ""],
  ["tags", zhEn("標籤", "Tags"), zhEn("用 ; 分隔", "separate with ;")],
  ["weight", zhEn("重量kg", "Weight (kg)"), ""],
];

// Header → field. Chinese labels, English keys and WooCommerce export names.
const ALIASES = {
  name: ["name", "名稱", "商品名稱", "產品名稱", "品名"],
  barcode: ["barcode", "條碼", "ean", "upc", "gtin", "gtin, upc, ean, or isbn"],
  sku: ["sku", "貨號"],
  category: ["category", "分類", "類別", "categories"],
  brand: ["brand", "品牌", "brands"],
  price: ["price", "售價", "價錢", "價格", "regular price", "零售價"],
  sale_price: ["sale_price", "sale price", "特價", "優惠價"],
  cost: ["cost", "成本", "入貨價", "cost price"],
  stock: ["stock", "庫存", "數量", "qty", "quantity", "in stock?"],
  low_stock: ["low_stock", "低庫存提示", "低庫存", "low stock amount", "low stock"],
  status: ["status", "狀態", "published"],
  sellable: ["sellable", "可賣"],
  track_stock: ["track_stock", "追蹤庫存", "manage stock?", "track stock"],
  description: ["description", "描述", "商品描述", "short description"],
  image_url: ["image_url", "圖片網址", "圖片", "image", "images", "image url"],
  tags: ["tags", "標籤"],
  weight: ["weight", "重量kg", "重量", "weight (kg)"],
};
const norm = (h) => String(h || "").replace(/^﻿/, "").trim().toLowerCase();
const FIELD_OF = Object.fromEntries(Object.entries(ALIASES).flatMap(([f, as]) => as.map(a => [a.toLowerCase(), f])));

export function parseCsv(text) {
  const rows = [];
  let row = [], cell = "", q = false;
  const s = text.replace(/^﻿/, "");
  const sep = (s.split("\n")[0].match(/\t/g) || []).length > (s.split("\n")[0].match(/,/g) || []).length ? "\t" : ",";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) {
      if (ch === '"') { if (s[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === sep) { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && s[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some(c => c.trim() !== "")) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some(c => c.trim() !== "")) rows.push(row);
  return rows;
}

const csvCell = (v) => { const x = String(v ?? ""); return /[",\n\r]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x; };
const yes = (v) => /^(1|y|yes|true|是|有|✓|visible)$/i.test(String(v).trim());
const no = (v) => /^(0|n|no|false|否|冇|無)$/i.test(String(v).trim());
const num = (v) => { const x = String(v ?? "").replace(/[$,\s]|HK\$/gi, ""); return x === "" ? null : Number(x); };
const statusOf = (v) => {
  const x = String(v || "").trim().toLowerCase();
  if (!x) return null;
  if (/^(上架|active|publish|published|1|是)$/.test(x)) return "active";
  if (/^(隱藏|hidden|private|-1)$/.test(x)) return "hidden";
  if (/^(下架|draft|0|否)$/.test(x)) return "draft";
  return "invalid";
};

export const STATUS_LABEL = { active: zhEn("上架", "active"), hidden: zhEn("隱藏", "hidden"), draft: zhEn("下架", "draft") };

export function downloadCsv(filename, rows) {
  const csv = "﻿" + rows.map(r => r.map(csvCell).join(",")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

const HEADER = COLUMNS.map(c => c[1]);
export const SAMPLE_ROWS = [
  HEADER,
  [zhEn("燕麥舒敏洗毛液 500ml", "Oat sensitive shampoo 500ml"), "4891234500011", "SH-OAT-500", zhEn("洗護", "Grooming care"), "PawsCare", "168", "", "82", "12", "3", zhEn("上架", "active"), zhEn("是", "yes"), zhEn("是", "yes"), zhEn("溫和燕麥配方，適合敏感皮膚", "Gentle oat formula for sensitive skin"), "", zhEn("敏感肌;低敏", "sensitive;hypoallergenic"), "0.55"],
  [zhEn("雞肉乾小食 100g", "Chicken jerky treats 100g"), "4891234500028", "TR-CHK-100", zhEn("小食", "Treats"), "HappyBite", "58", "48", "24", "30", "8", zhEn("上架", "active"), zhEn("是", "yes"), zhEn("是", "yes"), zhEn("100% 雞胸肉，低溫烘焙", "100% chicken breast, slow-baked"), "", "", "0.1"],
  [zhEn("寵物濕紙巾 80 片", "Pet wipes 80 sheets"), "", "AC-WIPE-80", zhEn("用品", "Supplies"), "PawsCare", "35", "", "14", "40", "10", zhEn("隱藏", "hidden"), zhEn("是", "yes"), zhEn("是", "yes"), zhEn("門市限定（網店唔顯示）", "In-store only (hidden online)"), "", "", ""],
  [zhEn("專業美容耳粉", "Professional ear powder"), "", "SV-EAR-01", zhEn("洗護", "Grooming care"), "GroomPro", "95", "", "40", "5", "2", zhEn("上架", "active"), zhEn("否", "no"), zhEn("是", "yes"), zhEn("美容用耗材，只計庫存唔賣", "Grooming supply — tracked in stock, not sold"), "", "", ""],
];

export const exportProducts = (products) => downloadCsv("pettobook-products-" + new Date().toISOString().slice(0, 10) + ".csv", [
  HEADER,
  ...products.map(p => [p.name, p.barcode, p.sku, p.category, p.brand, p.price, p.salePrice ?? "", p.cost, p.stock, p.lowStock,
    STATUS_LABEL[p.status] || zhEn("上架", "active"), p.sellable ? zhEn("是", "yes") : zhEn("否", "no"), p.trackStock ? zhEn("是", "yes") : zhEn("否", "no"), p.description,
    (p.images || [])[0] ? (typeof p.images[0] === "string" ? p.images[0] : p.images[0].src || "") : "", (p.tags || []).join(";"), p.web?.weight || ""]),
]);

// "售價 168→158" style hints for an existing product in the preview.
const FIELD_GET = {
  name: p => p.name, barcode: p => p.barcode, sku: p => p.sku, category: p => p.category, brand: p => p.brand,
  price: p => p.price, sale_price: p => p.salePrice ?? "", cost: p => p.cost, stock: p => p.stock, low_stock: p => p.lowStock,
  status: p => STATUS_LABEL[p.status], description: p => p.description,
};
function changesOf(x, mode, updSet) {
  const out = [];
  for (const [k, label] of COLUMNS) {
    if (!FIELD_GET[k] || x.r[k] === undefined) continue;
    if (mode === "update" && (!updSet.has(k) || x.r[k] === "")) continue;
    let nv = x.r[k];
    if (k === "status") nv = STATUS_LABEL[statusOf(nv)] || nv;
    const ov = FIELD_GET[k](x.match);
    if (["price", "sale_price", "cost", "stock", "low_stock"].includes(k) ? (nv === "" ? ov !== "" && ov != null : Number(num(nv)) !== Number(ov)) : String(nv) !== String(ov ?? "")) {
      out.push(label + " " + (k === "description" ? zhEn("改", "changed") : (ov === "" || ov == null ? zhEn("（空）", "(empty)") : ov) + "→" + (nv === "" ? zhEn("（清空）", "(cleared)") : nv)));
    }
  }
  return out.slice(0, 4);
}

export default function ProductImport({ open, onClose, tenant, products, sym, onDone }) {
  const [grid, setGrid] = useState(null);      // raw CSV cells, row 0 = header
  const [mapping, setMapping] = useState([]);   // per column: field key or ""
  const [step, setStep] = useState("pick");     // pick | map | preview
  const [fileName, setFileName] = useState("");
  // Matching + what to do with products that already exist.
  const [matchBy, setMatchBy] = useState({ barcode: true, sku: true, name: true });
  const [existingMode, setExistingMode] = useState("update");   // skip | update | replace
  const [updFields, setUpdFields] = useState(null);              // Set of fields to update (update mode)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const reset = () => { setGrid(null); setMapping([]); setStep("pick"); setFileName(""); setErr(""); setDone(null); };

  const readFile = async (file) => {
    reset();
    if (!file) return;
    setFileName(file.name);
    const buf = await file.arrayBuffer();
    // Excel on a Chinese Windows saves CSV as Big5 — fall back when UTF-8 shows replacement characters.
    let text = new TextDecoder("utf-8").decode(buf);
    if (text.includes("�")) { try { text = new TextDecoder("big5").decode(buf); } catch { /* keep utf-8 */ } }
    const g = parseCsv(text);
    if (g.length < 2) { setErr(zhEn("檔案冇資料行", "The file has no data rows")); return; }
    const used = new Set();
    const guess = g[0].map(h => { const f = FIELD_OF[norm(h)]; if (!f || used.has(f)) return ""; used.add(f); return f; });
    setGrid(g); setMapping(guess); setStep("map"); setUpdFields(null);
  };

  // Rows analysed with the owner's column matching.
  const rows = useMemo(() => {
    if (!grid || step !== "preview") return null;
    const nameKey = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");
    const byBarcode = new Map(products.filter(p => p.barcode).map(p => [p.barcode, p]));
    const bySku = new Map(products.filter(p => p.sku).map(p => [p.sku.toLowerCase(), p]));
    const byName = new Map(products.map(p => [nameKey(p.name), p]));
    const seen = new Map();   // "b:…" / "s:…" / "n:…" → first line
    return grid.slice(1).map((cells, i) => {
      const r = {};
      mapping.forEach((f, j) => { if (f && r[f] === undefined) r[f] = (cells[j] ?? "").trim(); });
      const errors = [];
      const name = r.name || "";
      if (!name) errors.push(zhEn("冇名稱", "No name"));
      const price = num(r.price);
      if (r.price !== undefined && r.price !== "" && !Number.isFinite(price)) errors.push(zhEn("售價唔係數字", "Price isn't a number"));
      for (const f of ["sale_price", "cost", "stock", "low_stock"]) if (r[f] && !Number.isFinite(num(r[f]))) errors.push(COLUMNS.find(c => c[0] === f)[1] + zhEn("唔係數字", " isn't a number"));
      const st = statusOf(r.status);
      if (st === "invalid") errors.push(zhEn("狀態要係 上架 / 隱藏 / 下架", "Status must be active / hidden / draft"));
      const barcode = (r.barcode || "").replace(/\.0$/, "");
      if (barcode && /e\+/i.test(barcode)) errors.push(zhEn("條碼被 Excel 變成科學記數，請將欄位設為文字", "Excel turned the barcode into scientific notation — set the column to Text"));
      // Same product twice in the file → keep the first, skip the rest.
      const keys = [matchBy.barcode && barcode && "b:" + barcode, matchBy.sku && r.sku && "s:" + r.sku.toLowerCase(), matchBy.name && name && "n:" + nameKey(name)].filter(Boolean);
      const dup = keys.map(k => seen.get(k)).find(v => v !== undefined);
      if (dup !== undefined) errors.push(zhEn("同第 ", "Same as row ") + dup + zhEn(" 行係同一件貨（重複，略過）", " (same product — duplicate, skipped)"));
      else keys.forEach(k => seen.set(k, i + 2));
      const match = (matchBy.barcode && barcode && byBarcode.get(barcode))
        || (matchBy.sku && r.sku && bySku.get(r.sku.toLowerCase()))
        || (matchBy.name && name && byName.get(nameKey(name))) || null;
      const matchedBy = !match ? null : (matchBy.barcode && barcode && byBarcode.get(barcode)) ? zhEn("條碼", "Barcode") : (matchBy.sku && r.sku && bySku.get(r.sku.toLowerCase())) ? "SKU" : zhEn("名稱", "Name");
      return { line: i + 2, r, name, price, barcode, status: st === "invalid" ? null : st, match, matchedBy, errors, dup: dup !== undefined };
    });
  }, [grid, mapping, step, products, matchBy]);

  const summary = useMemo(() => {
    if (!rows) return null;
    const ok = rows.filter(x => !x.errors.length);
    const upd = ok.filter(x => x.match).length;
    return { total: rows.length, bad: rows.length - ok.length, add: ok.filter(x => !x.match).length, upd, write: ok.filter(x => !x.match).length + (existingMode === "skip" ? 0 : upd) };
  }, [rows, existingMode]);
  const mappedFields = useMemo(() => mapping.filter(Boolean), [mapping]);
  const updSet = updFields || new Set(mappedFields.filter(f => f !== "stock"));

  const run = async () => {
    setBusy(true); setErr("");
    try {
      const good = rows.filter(x => !x.errors.length && (!x.match || existingMode !== "skip"));
      const toSave = [], opening = [], counts = [];
      for (const x of good) {
        const r = x.r, base = x.match || {};
        const id = base.id || newId();
        // New product: every non-empty cell. Update: only the ticked fields, and
        // an empty cell keeps the current value. Replace: every matched column
        // wins, an empty cell clears it.
        const mapped = (f) => r[f] !== undefined;
        const has = !x.match ? (f) => mapped(f) && r[f] !== ""
          : existingMode === "replace" ? (f) => mapped(f)
          : (f) => updSet.has(f) && mapped(f) && r[f] !== "";
        const img = has("image_url") ? (r.image_url ? [r.image_url] : []) : (base.images || []);
        toSave.push({
          id, tenantId: tenant.id, parentId: base.parentId || null, optionValues: base.optionValues || {},
          name: !x.match || has("name") ? x.name : base.name, sku: has("sku") ? r.sku : (base.sku || ""), barcode: has("barcode") ? x.barcode : (base.barcode || ""),
          ...(() => {
            // Same name as now → keep the linked id; new text → the DB trigger
            // finds or creates it; column not in the file → keep id + label.
            const cat = has("category") ? r.category.split(/[>,|]/)[0].trim() : (base.category || "");
            const brand = has("brand") ? r.brand : (base.brand || "");
            const same = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
            return {
              category: cat, categoryId: base.categoryId && same(cat, base.category) ? base.categoryId : null,
              brand, brandId: base.brandId && same(brand, base.brand) ? base.brandId : null,
            };
          })(),
          categories: base.categories || [], season: base.season || "",
          tags: has("tags") ? r.tags.split(/[;；,，]/).map(t => t.trim()).filter(Boolean) : (base.tags || []),
          status: (has("status") && x.status) || base.status || "active",
          sellable: has("sellable") ? !no(r.sellable) : (base.sellable ?? true),
          price: has("price") ? round2(x.price || 0) : (base.price ?? 0),
          salePrice: has("sale_price") ? (r.sale_price === "" ? null : round2(num(r.sale_price))) : (base.salePrice ?? null),
          saleFrom: base.saleFrom || null, saleTo: base.saleTo || null,
          cost: has("cost") ? round2(num(r.cost) || 0) : (base.cost ?? 0),
          trackStock: has("track_stock") ? !no(r.track_stock) : (base.trackStock ?? true),
          lowStock: has("low_stock") ? (num(r.low_stock) || 0) : (base.lowStock ?? 0),
          reorderQty: base.reorderQty ?? 0, allowBackorder: base.allowBackorder ?? false,
          images: img, description: has("description") ? r.description : (base.description || ""),
          web: { ...(base.web || {}), ...(has("weight") ? { weight: r.weight } : null) },
          sortOrder: base.sortOrder ?? 0,
        });
        // WooCommerce's "In stock?" is 1/0, not a quantity — only numbers > 1 or real stock columns count.
        if (mapped("stock") && r.stock !== "" && Number.isFinite(num(r.stock))) {
          const q = num(r.stock);
          if (!x.match && q > 0) opening.push({ productId: id, qty: q, ...(r.cost ? { cost: num(r.cost) } : null) });
          if (x.match && has("stock") && q !== x.match.stock) counts.push({ productId: id, count: q });
        }
      }
      let saved = await bulkUpsertPosProducts(toSave);
      const stockById = {};
      for (let i = 0; i < opening.length; i += 200) for (const s of await adjustStock({ tenantId: tenant.id, kind: "opening", note: "CSV 匯入開倉", items: opening.slice(i, i + 200) }) || []) stockById[s.productId] = s;
      for (let i = 0; i < counts.length; i += 200) for (const s of await adjustStock({ tenantId: tenant.id, kind: "count", note: "CSV 匯入盤點", items: counts.slice(i, i + 200) }) || []) stockById[s.productId] = s;
      saved = saved.map(p => stockById[p.id] ? { ...p, stock: Number(stockById[p.id].stock), cost: Number(stockById[p.id].cost) } : p);
      setDone({ added: good.filter(x => !x.match).length, updated: good.filter(x => x.match).length, stock: opening.length + counts.length });
      onDone(saved);
    } catch (e) {
      setErr(/products_tenant_barcode_uniq|duplicate key/.test(String(e?.message)) ? zhEn("有條碼已經俾其他商品用緊 — 請檢查檔案", "A barcode is already used by another product — please check the file") : posErrorText(e));
    } finally { setBusy(false); }
  };

  const close = () => { if (busy) return; reset(); onClose(); };

  return (
    <Sheet open={open} onClose={close} title={zhEn("匯入商品 (CSV)", "Import products (CSV)")} maxWidth={680}
      footer={step === "map" && !done ? (
        <div>
          {!mapping.includes("name") && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, margin: "0 0 8px", textAlign: "center" }}>{zhEn("請揀邊一欄係「名稱」", "Choose which column is \"Name\"")}</p>}
          <Btn style={{ width: "100%" }} disabled={!mapping.includes("name")} onClick={() => setStep("preview")}>{zhEn("下一步：預覽", "Next: preview")} {grid.length - 1} {zhEn("行", "rows")}</Btn>
        </div>
      ) : rows && !done ? (
        <div>
          {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, margin: "0 0 8px", textAlign: "center" }}>{err}</p>}
          <Btn style={{ width: "100%" }} disabled={busy || !summary || summary.write === 0} onClick={run}>
            {busy ? zhEn("匯入中…", "Importing…") : zhEn("匯入 ", "Import ") + summary.write + zhEn(" 件", " items") + (summary.bad ? zhEn("（略過 ", " (skipping ") + summary.bad + zhEn(" 行）", " rows)") : "")}
          </Btn>
        </div>
      ) : done ? <Btn style={{ width: "100%" }} onClick={close}>{zhEn("完成", "Done")}</Btn> : null}>
      {done ? (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 48 }}>✅</div>
          <p style={{ fontSize: 18, fontWeight: 900, color: C.ink, margin: "8px 0 4px" }}>{zhEn("匯入完成", "Import complete")}</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: C.sub, margin: 0 }}>{zhEn("新增", "Add")} {done.added} {zhEn("· 更新", "· updated")} {done.updated}{done.stock ? zhEn(" · 庫存記錄 ", " · stock records ") + done.stock : ""}</p>
        </div>
      ) : step === "map" ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <FileText size={18} color={C.sub} />
            <span style={{ flex: 1, fontWeight: 900, color: C.ink, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileName} · {grid.length - 1} {zhEn("行", "rows")}</span>
            <Btn kind="ghost" small onClick={reset}>{zhEn("換檔", "Change file")}</Btn>
          </div>
          <p style={{ fontSize: 13, fontWeight: 700, color: C.sub, margin: "0 2px 10px", lineHeight: 1.5 }}>{zhEn("配對欄位：左邊係你檔案嘅欄，右邊揀對應嘅商品資料。唔需要嘅欄揀「略過」。已自動配對認得嘅欄名。", "Match columns: your file's columns are on the left; choose the matching product field on the right. Pick \"Skip\" for columns you don't need. Recognised column names are matched automatically.")}</p>
          {grid[0].map((h, j) => {
            const samples = grid.slice(1, 4).map(r => (r[j] ?? "").trim()).filter(Boolean);
            const taken = new Set(mapping.filter((f, k) => f && k !== j));
            return (
              <div key={j} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 8, background: "#fff", border: "1px solid " + (mapping[j] ? C.accent : C.line), borderRadius: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(h).replace(/^\uFEFF/, "") || zhEn("（第 ", "(column ") + (j + 1) + zhEn(" 欄）", ")")}</p>
                  <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700, color: C.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{samples.length ? zhEn("例：", "e.g. ") + samples.join(" · ") : zhEn("（空）", "(empty)")}</p>
                </div>
                <span style={{ color: C.faint, fontWeight: 900 }}>→</span>
                <select value={mapping[j] || ""} onChange={e => setMapping(m => m.map((f, k) => k === j ? e.target.value : f))}
                  style={{ width: 128, border: "1.5px solid " + C.line, borderRadius: 10, padding: "8px 6px", fontSize: 13.5, fontWeight: 800, fontFamily: "inherit", color: mapping[j] ? C.ink : C.sub, background: "#fff" }}>
                  <option value="">{zhEn("— 略過 —", "— skip —")}</option>
                  {COLUMNS.map(([k, label, note]) => <option key={k} value={k} disabled={taken.has(k)}>{label}{note === "必填" ? " *" : ""}</option>)}
                </select>
              </div>
            );
          })}
        </>
      ) : !rows ? (
        <>
          <div style={{ background: C.accentSoft, borderRadius: 16, padding: 14, marginBottom: 14 }}>
            <p style={{ margin: "0 0 6px", fontWeight: 900, color: C.ink }}>{zhEn("3 步", "3 steps")}</p>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.ink, lineHeight: 1.7 }}>
              {zhEn("1. 下載範例檔，用 Excel / Numbers 開", "1. Download the sample file and open it in Excel / Numbers")}<br />
              {zhEn("2. 每行一件商品，填好名稱同售價（其他欄可留空）", "2. One product per row — fill in the name and price (other columns can stay empty)")}<br />
              {zhEn("3. 儲存為 CSV，喺度揀檔案 → 預覽 → 確認匯入", "3. Save as CSV, choose the file here → preview → confirm the import")}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <Btn kind="ghost" style={{ flex: 1 }} onClick={() => downloadCsv("pettobook-products-sample.csv", SAMPLE_ROWS)}><Download size={18} /> {zhEn("下載範例檔", "Download sample file")}</Btn>
            <label style={{ flex: 1.3, display: "flex" }}>
              <input type="file" accept=".csv,text/csv,.tsv,.txt" style={{ display: "none" }} onChange={e => { readFile(e.target.files?.[0]); e.target.value = ""; }} />
              <span style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderRadius: 16, padding: "14px 18px", background: C.grad, color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", boxShadow: "0 8px 20px rgba(255,92,138,0.28)" }}><Upload size={18} /> {zhEn("揀 CSV 檔", "Choose a CSV file")}</span>
            </label>
          </div>
          {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13 }}>{err}</p>}
          <Label>{zhEn("欄位", "Columns")}</Label>
          <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: "4px 12px" }}>
            {COLUMNS.map(([k, label, note]) => (
              <div key={k} style={{ display: "flex", gap: 8, padding: "7px 0", borderBottom: "1px solid " + C.line, fontSize: 13 }}>
                <span style={{ width: 92, fontWeight: 900, color: C.ink }}>{label}</span>
                <span style={{ flex: 1, fontWeight: 700, color: note === "必填" ? C.accent : C.sub }}>{note || zhEn("可留空", "optional")}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "10px 2px 0", lineHeight: 1.6 }}>
            {zhEn("• 有相同條碼（冇條碼就睇 SKU）嘅商品會更新，其他會新增。", "• Products with the same barcode (or SKU when there is no barcode) are updated; the rest are added.")}<br />
            {zhEn("• 「庫存」：新商品會記錄為開倉；現有商品要開「更新庫存」先會改（記錄為盤點）。", "• \"Stock\": new products are recorded as opening stock; existing products only change if you switch on \"update stock\" (recorded as a count).")}<br />
            {zhEn("• 都讀得 WooCommerce 匯出嘅商品 CSV。", "• WooCommerce product export CSVs can be read too.")}<br />
            {zhEn("• 條碼欄喺 Excel 請設為「文字」，否則長條碼會變成 4.89E+12。", "• Set the barcode column to \"Text\" in Excel, or long barcodes turn into 4.89E+12.")}
          </p>
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <FileText size={18} color={C.sub} />
            <span style={{ flex: 1, fontWeight: 900, color: C.ink, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileName}</span>
            <Btn kind="ghost" small onClick={() => setStep("map")}>{zhEn("← 配對欄位", "← Match columns")}</Btn>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 12 }}>
            {[[zhEn("新增", "New"), summary.add, C.green], [zhEn("更新", "Update"), summary.upd, C.blue], [zhEn("有錯", "Errors"), summary.bad, summary.bad ? C.red : C.faint]].map(([k, v, col]) => (
              <div key={k} style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 14, padding: "10px 8px", textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 900, color: C.sub }}>{k}</p>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: col }}>{v}</p>
              </div>
            ))}
          </div>
          <Label>{zhEn("點樣認出同一件貨（避免重複）", "How to recognise the same product (avoids duplicates)")}</Label>
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {[["barcode", zhEn("條碼", "Barcode")], ["sku", "SKU"], ["name", zhEn("名稱完全一樣", "Exactly the same name")]].map(([k, l]) => (
              <Chip key={k} on={matchBy[k]} onClick={() => setMatchBy(m => ({ ...m, [k]: !m[k] }))}>{matchBy[k] ? "✓ " : ""}{l}</Chip>
            ))}
          </div>
          {summary.upd > 0 && (
            <>
              <Label>{zhEn("已有嘅", "Existing")} {summary.upd} {zhEn("件商品", "products")}</Label>
              <Seg value={existingMode} onChange={setExistingMode} style={{ marginBottom: 8 }} options={[["skip", zhEn("略過（只新增）", "Skip (add only)")], ["update", zhEn("更新指定欄位", "Update chosen columns")], ["replace", zhEn("全部取代", "Replace all")]]} />
              <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "0 2px 10px", lineHeight: 1.5 }}>
                {existingMode === "skip" && zhEn("已有嘅商品完全唔郁，只會新增未有嘅。重複匯入同一個檔都唔會出現兩件。", "Existing products are left alone — only new ones are added. Importing the same file again won't create duplicates.")}
                {existingMode === "update" && zhEn("只更新下面剔咗嘅欄位；檔案入面空白嘅格會保留原本資料。", "Only the columns ticked below are updated; empty cells in the file keep the existing data.")}
                {existingMode === "replace" && zhEn("所有配對咗嘅欄位都用檔案嘅資料，空白格會清空原本資料（未配對嘅欄位保留）。", "Every matched column takes the file's data, and empty cells clear the existing data (unmatched columns are kept).")}
              </p>
              {existingMode === "update" && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {COLUMNS.filter(([k]) => mappedFields.includes(k)).map(([k, label]) => {
                    const on = updSet.has(k);
                    return <Chip key={k} on={on} color={C.blue} onClick={() => { const n = new Set(updSet); on ? n.delete(k) : n.add(k); setUpdFields(n); }}>{on ? "✓ " : ""}{label}{k === "stock" ? zhEn("（盤點）", " (count)") : ""}</Chip>;
                  })}
                </div>
              )}
            </>
          )}
          <Label>{zhEn("預覽", "Preview")}</Label>
          {rows.slice(0, 300).map(x => (
            <div key={x.line} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", borderBottom: "1px solid " + C.line, background: x.errors.length ? C.redSoft : "transparent", borderRadius: x.errors.length ? 8 : 0 }}>
              <span style={{ width: 30, fontSize: 11, fontWeight: 800, color: C.faint }}>{x.line}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.name || zhEn("（冇名稱）", "(no name)")}</p>
                <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700, color: x.errors.length ? C.red : C.sub }}>
                  {x.errors.length ? x.errors.join("；")
                    : x.match ? (existingMode === "skip" ? zhEn("已有（", "Exists (") + x.matchedBy + zhEn("相同）· 略過", " match) · skipped") : (changesOf(x, existingMode, updSet).join(" · ") || zhEn("已有（", "Exists (") + x.matchedBy + zhEn("相同）· 冇改動", " match) · no changes")))
                    : [x.barcode, x.r.sku, x.r.category, x.r.stock !== undefined && x.r.stock !== "" ? zhEn("庫存 ", "Stock ") + x.r.stock : ""].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              {Number.isFinite(x.price) && <span style={{ fontWeight: 900, color: C.ink, fontSize: 13 }}>{money(x.price, sym)}</span>}
              {!x.errors.length && <Pill tone={!x.match ? "green" : existingMode === "skip" ? "grey" : "blue"}>{!x.match ? zhEn("新增", "Add") : existingMode === "skip" ? zhEn("略過", "Skip") : existingMode === "replace" ? zhEn("取代", "Replace") : zhEn("更新", "Update")}</Pill>}
              {x.status && <Pill tone={x.status === "active" ? "green" : x.status === "hidden" ? "amber" : "grey"}>{STATUS_LABEL[x.status]}</Pill>}
            </div>
          ))}
          {rows.length > 300 && <p style={{ fontSize: 12, fontWeight: 800, color: C.sub, textAlign: "center" }}>{zhEn("…仲有", "…and")} {rows.length - 300} {zhEn("行（都會匯入）", "more rows (also imported)")}</p>}
        </>
      )}
    </Sheet>
  );
}
